import { useCallback, useEffect, useRef } from 'react';
import useDebouncedCallback from '../useDebounce';

export const useLLMFormFieldsValidation = ({
  form,
  configValues,
  configPath,
  optionsPath,
  setCanSubmit,
  setHasOptionsError
}) => {

  // Used isMounted to avoid validation the first time we open the form
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const performValidation = useCallback(async () => {
    if (!form.isFieldsTouched()) {
      return;
    }

    try {
      // If no error when validation the form field then proceed normally
      await form.validateFields();
      if (isMounted.current) {
        setCanSubmit(true);
        setHasOptionsError(false);
      }
    } catch (errorInfo) {
      if (!isMounted.current) {
        return;
      }

      const { errorFields } = errorInfo;

      // Check for errors in the 'configuration' level to catch for prompt, json and model level errors
      const hasConfigErrors = errorFields.some(({ name }) => {
        if (name.length < configPath.length) {
          return false;
        }
        return configPath.every((part, index) => part === name[index]);
      });

      setCanSubmit(!hasConfigErrors);

      // Check for any errors in the ['configuration', 'options'] level specifically
      const hasOptError = errorFields.some(({ name }) => {
        if (name.length < optionsPath.length) {
          return false;
        }
        return optionsPath.every((part, index) => part === name[index]);
      });
      setHasOptionsError(hasOptError);
    }
  }, [form, configPath, optionsPath, setCanSubmit, setHasOptionsError]);

  // Debounce to validate in every single value change in the forms for performance reasons
  const debouncedValidate = useDebouncedCallback(performValidation, 600);

  useEffect(() => {
    debouncedValidate();
  }, [configValues, debouncedValidate]);
};
